/**
 * ChecklistImportWizard.js
 *
 * Multi-step wizard for AI-powered checklist import.
 * Guides user through: Type → Upload → Process → Review → Warnings → Approval → Confirm
 */

import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, FileUp, Loader2 } from 'lucide-react';
import { useChecklistImport } from '../../../../../hooks/useChecklistImport';
import { IMPORT_WIZARD_STEPS } from '../../../../../config/operations/checklistImportConfig';

// Step components
import SelectTypeStep from './steps/SelectTypeStep';
import UploadDocumentStep from './steps/UploadDocumentStep';
import ProcessingStep from './steps/ProcessingStep';
import ReviewItemsStep from './steps/ReviewItemsStep';
import RegulatoryWarningsStep from './steps/RegulatoryWarningsStep';
import ApprovalStep from './steps/ApprovalStep';
import ConfirmStep from './steps/ConfirmStep';

/**
 * Step indicator component
 */
const StepIndicator = ({ steps, currentIndex }) => (
  <div className="flex items-center justify-center gap-1 px-4 py-3 bg-slate-50 border-b border-slate-200">
    {steps.map((step, index) => {
      const isActive = index === currentIndex;
      const isComplete = index < currentIndex;

      return (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center">
            <div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                transition-colors
                ${isActive ? 'bg-cyan-600 text-white' : ''}
                ${isComplete ? 'bg-green-500 text-white' : ''}
                ${!isActive && !isComplete ? 'bg-slate-200 text-slate-500' : ''}
              `}
            >
              {isComplete ? '✓' : index + 1}
            </div>
            <span className={`text-xs mt-1 hidden sm:block ${isActive ? 'text-cyan-600 font-medium' : 'text-slate-400'}`}>
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 h-0.5 ${isComplete ? 'bg-green-500' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/**
 * Main wizard component
 */
const ChecklistImportWizard = ({ isOpen, onClose, onComplete, initialType = null }) => {
  const {
    // State
    currentStep,
    checklistType,
    uploadedFile,
    extractionResult,
    editedItems,
    conflicts,
    approvalData,
    isProcessing,
    error,

    // Actions
    setChecklistType,
    setUploadedFile,
    updateItem,
    addItem,
    removeItem,
    reorderItems,
    setApprovalData,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    canProceed,

    // Operations
    startExtraction,
    finalizeImport,
    reset
  } = useChecklistImport();

  // Handle pre-selection of checklist type
  useEffect(() => {
    if (initialType && isOpen) {
      setChecklistType(initialType);
    }
  }, [initialType, isOpen, setChecklistType]);

  // Close and reset handler
  const handleClose = () => {
    reset();
    onClose();
  };

  // Complete handler
  const handleComplete = async () => {
    const result = await finalizeImport();
    if (result.success) {
      onComplete?.(result.checklist);
      handleClose();
    }
  };

  // Get current step index
  const currentStepIndex = IMPORT_WIZARD_STEPS.findIndex(s => s.id === currentStep);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'select_type':
        return (
          <SelectTypeStep
            selectedType={checklistType}
            onSelectType={setChecklistType}
          />
        );

      case 'upload':
        return (
          <UploadDocumentStep
            file={uploadedFile}
            onFileSelect={setUploadedFile}
            checklistType={checklistType}
            error={error}
          />
        );

      case 'processing':
        return (
          <ProcessingStep
            file={uploadedFile}
            isProcessing={isProcessing}
            error={error}
            onRetry={startExtraction}
          />
        );

      case 'review':
        return (
          <ReviewItemsStep
            items={editedItems}
            extractionResult={extractionResult}
            onUpdateItem={updateItem}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onReorderItems={reorderItems}
          />
        );

      case 'warnings':
        return (
          <RegulatoryWarningsStep
            conflicts={conflicts}
            items={editedItems}
          />
        );

      case 'approval':
        return (
          <ApprovalStep
            approvalData={approvalData}
            onUpdateApproval={setApprovalData}
            checklistType={checklistType}
            itemCount={editedItems?.length || 0}
          />
        );

      case 'confirm':
        return (
          <ConfirmStep
            checklistType={checklistType}
            items={editedItems}
            approvalData={approvalData}
            extractionResult={extractionResult}
            uploadedFile={uploadedFile}
          />
        );

      default:
        return <div className="p-6 text-center text-slate-500">Unknown step</div>;
    }
  };

  // Button labels for each step
  const getNextButtonLabel = () => {
    switch (currentStep) {
      case 'select_type':
        return 'Continue';
      case 'upload':
        return 'Extract Items';
      case 'processing':
        return isProcessing ? 'Processing...' : 'Continue';
      case 'review':
        return 'Check Conflicts';
      case 'warnings':
        return 'Request Approval';
      case 'approval':
        return 'Review & Save';
      case 'confirm':
        return 'Import Checklist';
      default:
        return 'Next';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
              <FileUp className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Import Checklist</h2>
              <p className="text-sm text-slate-500">AI-powered document extraction</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator steps={IMPORT_WIZARD_STEPS} currentIndex={currentStepIndex} />

        {/* Content area */}
        <div className="flex-1 overflow-y-auto">
          {renderStepContent()}
        </div>

        {/* Footer with navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={currentStepIndex === 0 ? handleClose : goToPreviousStep}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
            disabled={isProcessing}
          >
            <ChevronLeft className="h-4 w-4" />
            {currentStepIndex === 0 ? 'Cancel' : 'Back'}
          </button>

          {error && currentStep !== 'processing' && (
            <p className="text-sm text-red-600 flex-1 text-center">{error}</p>
          )}

          <button
            onClick={currentStep === 'confirm' ? handleComplete : goToNextStep}
            disabled={!canProceed() || isProcessing}
            className={`
              flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-colors
              ${canProceed() && !isProcessing
                ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }
            `}
          >
            {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
            {getNextButtonLabel()}
            {!isProcessing && currentStep !== 'confirm' && <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistImportWizard;
